import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AmountInput } from '../../../../core/shared/directives/amount-input.directive';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { distinctUntilChanged, finalize, forkJoin, Subscription } from 'rxjs';

import {
  CalculateDisbursementRepaymentPlanItemPayload,
  CalculateDisbursementRepaymentPlanPayload,
  CalculateDisbursementRepaymentPlanTermPayload,
  CraditService,
} from '../../shared/services/cradit.service';
import { extractBaseExposure } from '../../shared/utils/repayment-api.util';
import { DropdownOption, PKL_GRP } from '../../../../core/shared/models/picklist.model';
import { PickListService } from '../../../../core/shared/services/picklist.service';
import { ToastService } from '../../../../core/shared/services/toast.service';
import {
  RepaymentCalculationResponse,
  RepaymentCalculationTrancheRow,
  RepaymentPlanStateService,
  SaveRepaymentPlanPayload,
  SaveRepaymentPlanTermPayload,
} from './repayment-plan-state.service';

interface ProposedDisbursementApiRow {
  prpsdDsbmntSeq: number;
  schMth: string;
  prpsdDsbmntAmt: number;
  /** Repayment term type (PKL_GRP.FACILITY_TYPE) this disbursement belongs to. */
  termRefCdSeq: number;
}

/** One saved installment row from GET /rpymnt-plan/get-prpsd-rpymnt/{crdtPrpslSeq}. */
interface SavedRepaymentInstallmentRow {
  prpsdDsbmntSeq: number;
  schRpymntMth: string;
  prpsdRpymntAmnt: number;
  qrtr: string;
}

interface RepaymentSchedule {
  [period: string]: number;
}

interface RepaymentRow {
  prpsdDsbmntSeq: number;
  quarter: string;
  month: string;
  trancheAmount: number;
  schedule: RepaymentSchedule;
  /** Repayment term type (PKL_GRP.FACILITY_TYPE) this tranche belongs to — shown in the Type column. */
  termRefCdSeq: number;
}

/** One repayment term type's (e.g. Conventional / Islamic) own schedule table. */
interface RepaymentTermGroup {
  termRefCdSeq: number;
  rows: RepaymentRow[];
  /** Only the periods this term's rows have installments in. */
  columns: string[];
}

@Component({
  selector: 'app-repayment',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe, AmountInput],
  templateUrl: './repayment.html',
  styleUrl: './repayment.css',
})
export class Repayment implements OnInit, OnChanges, OnDestroy {
  @Input() editable = false;
  @Input() crdtPrpslSeq: number | null = null;
  @Input() sectionId = '';

  columns: string[] = [];
  repaymentRows: RepaymentRow[] = [];
  totalDisbursementAmount = 0;
  openingExposure = 0;

  saving = false;
  calculating = false;
  isDirty = false;
  errorMessage = '';
  successMessage = '';

  showPreviewModal = false;
  previewEditMode = false;

  termOptions: DropdownOption[] = [];

  private loadedOpeningExposureSequence: number | null = null;
  private loadedRepaymentPlanSequence: number | null = null;
  private readonly subscriptions = new Subscription();
  private readonly tolerance = 0.01;

  constructor(
    private creditService: CraditService,
    private repaymentPlanState: RepaymentPlanStateService,
    private pickListService: PickListService,
    private toast: ToastService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.loadTermOptions();

    const planSubscription = this.repaymentPlanState.calculatedPlan$
      .subscribe((plan) => {
        if (!plan) {
          return;
        }

        const currentSequence = this.getCurrentProposalSequence();

        if (
          currentSequence > 0 &&
          Number(plan.crdtPrpslSeq) !== currentSequence
        ) {
          return;
        }

        this.applyCalculatedPlan(plan);
      });

    this.subscriptions.add(planSubscription);

    const sequenceSubscription = this.creditService.crdtPrpslSeq$
      .pipe(distinctUntilChanged())
      .subscribe((sequence) => {
        const normalizedSequence = Number(sequence ?? 0);

        if (
          !Number.isFinite(normalizedSequence) ||
          normalizedSequence <= 0
        ) {
          return;
        }

        this.loadOpeningExposure(normalizedSequence);
        this.loadSavedRepaymentPlan(normalizedSequence);
      });

    this.subscriptions.add(sequenceSubscription);

    const currentSequence = this.getCurrentProposalSequence();

    if (currentSequence > 0) {
      this.loadOpeningExposure(currentSequence);
      this.loadSavedRepaymentPlan(currentSequence);
    }

    const saveSectionSubscription = this.creditService.saveSectionTrigger$.subscribe(
      (triggeredSectionId) => {
        if (triggeredSectionId === this.sectionId) {
          this.saveRepaymentPlan();
        }
      },
    );

    this.subscriptions.add(saveSectionSubscription);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['crdtPrpslSeq']) {
      return;
    }

    const currentSequence = this.getCurrentProposalSequence();
    const currentPlan = this.repaymentPlanState.currentPlan;

    if (
      currentPlan &&
      Number(currentPlan.crdtPrpslSeq) === currentSequence
    ) {
      this.applyCalculatedPlan(currentPlan);
    }

    if (currentSequence > 0) {
      this.loadOpeningExposure(currentSequence);
      this.loadSavedRepaymentPlan(currentSequence);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get grandTotal(): number {
    return this.roundAmount(
      this.repaymentRows.reduce(
        (total, row) => total + this.getRowTotal(row),
        0,
      ),
    );
  }

  get remainingAmount(): number {
    return this.roundAmount(
      this.totalDisbursementAmount - this.grandTotal,
    );
  }

  get isGrandTotalExceeded(): boolean {
    return (
      this.grandTotal >
      this.totalDisbursementAmount + this.tolerance
    );
  }

  get isGrandTotalUnderAllocated(): boolean {
    return (
      this.grandTotal <
      this.totalDisbursementAmount - this.tolerance
    );
  }

  get hasRowLimitError(): boolean {
    return this.repaymentRows.some((row) =>
      this.isRowTotalExceeded(row),
    );
  }

  get hasRowUnderAllocationError(): boolean {
    return this.repaymentRows.some((row) =>
      this.isRowTotalUnderAllocated(row),
    );
  }

  get hasValidationError(): boolean {
    return (
      this.isGrandTotalExceeded ||
      this.hasRowLimitError ||
      this.isGrandTotalUnderAllocated ||
      this.hasRowUnderAllocationError
    );
  }

  /**
   * Rows split into one schedule table per repayment term type, in the order
   * each type first appears. Rebuilt only when `repaymentRows` is replaced.
   */
  get termGroups(): RepaymentTermGroup[] {
    if (this.termGroupsSource !== this.repaymentRows) {
      const rowsByTerm = new Map<number, RepaymentRow[]>();

      this.repaymentRows.forEach((row) => {
        const key = Number(row.termRefCdSeq ?? 0);
        rowsByTerm.set(key, [...(rowsByTerm.get(key) ?? []), row]);
      });

      this.termGroupsCache = Array.from(rowsByTerm.entries()).map(
        ([termRefCdSeq, rows]) => ({
          termRefCdSeq,
          rows,
          columns: this.extractColumns(rows),
        }),
      );
      this.termGroupsSource = this.repaymentRows;
    }

    return this.termGroupsCache;
  }

  private termGroupsSource: RepaymentRow[] | null = null;
  private termGroupsCache: RepaymentTermGroup[] = [];

  getColumnTotalFor(rows: RepaymentRow[], period: string): number {
    return this.roundAmount(
      rows.reduce(
        (total, row) => total + this.toAmount(row.schedule[period]),
        0,
      ),
    );
  }

  getGrandTotalFor(rows: RepaymentRow[]): number {
    return this.roundAmount(
      rows.reduce((total, row) => total + this.getRowTotal(row), 0),
    );
  }

  hasInstallment(row: RepaymentRow, period: string): boolean {
    return Object.prototype.hasOwnProperty.call(row.schedule, period);
  }

  getRowTotal(row: RepaymentRow): number {
    return this.roundAmount(
      Object.values(row.schedule).reduce((total, value) => {
        const amount = Number(value ?? 0);
        return Number.isFinite(amount) ? total + amount : total;
      }, 0),
    );
  }

  isRowTotalExceeded(row: RepaymentRow): boolean {
    return (
      this.getRowTotal(row) >
      row.trancheAmount + this.tolerance
    );
  }

  isRowTotalUnderAllocated(row: RepaymentRow): boolean {
    return (
      this.getRowTotal(row) <
      row.trancheAmount - this.tolerance
    );
  }

  onInstallmentChange(
    row: RepaymentRow,
    period: string,
    value: number | string | null,
  ): void {
    const numericValue = Number(value ?? 0);

    row.schedule[period] =
      Number.isFinite(numericValue) && numericValue >= 0
        ? this.roundAmount(numericValue)
        : 0;

    this.isDirty = true;
    this.successMessage = '';
    this.errorMessage = '';
  }

  // Same POST /crdt-prpsl/clclate-rpymnt-plan call and payload shape the
  // (now unlisted) Calculate Repayment Plan section used to send — gathers
  // terms, previous max exposure and proposed disbursements fresh, then
  // publishes the result into `repaymentPlanState`, which this component's
  // own `calculatedPlan$` subscription (see ngOnInit) picks up automatically.
  calculateRepaymentPlan(): void {
    if (!this.editable) {
      return;
    }

    this.errorMessage = '';
    this.successMessage = '';

    const proposalSequence =
      this.getCurrentProposalSequence();

    if (proposalSequence <= 0) {
      this.errorMessage =
        this.translate.instant('DISB_REPAYMENT.ERR_PROPOSAL_SEQ_REQUIRED');

      this.toast.error(this.errorMessage);
      this.reportValidationError(
        this.errorMessage,
      );

      return;
    }

    this.calculating = true;

    const inputsSubscription = forkJoin({
      terms: this.creditService.getCreditTermParameters(proposalSequence),
      exposure: this.creditService.getPreviousMaxExposure(proposalSequence),
      disbursements: this.creditService.getProposedDisbursements(proposalSequence),
    }).subscribe({
      next: ({ terms, exposure, disbursements }) => {
        const termsSummary = this.extractTermsSummary(terms);

        if (
          !termsSummary ||
          !Number.isFinite(termsSummary.rpymntFrqncy) ||
          termsSummary.rpymntFrqncy <= 0
        ) {
          this.calculating = false;

          this.errorMessage =
            this.translate.instant('DISB_REPAYMENT.ERR_REPAYMENT_FREQUENCY_MISSING');

          this.toast.error(this.errorMessage);
          this.reportValidationError(this.errorMessage);
          return;
        }

        const disbursementRows =
          this.extractSavedDisbursements(disbursements)
            .filter((row) => row.prpsdDsbmntSeq > 0);

        if (disbursementRows.length === 0) {
          this.calculating = false;

          this.errorMessage =
            this.translate.instant('DISB_REPAYMENT.ERR_NO_PROPOSED_DISBURSEMENTS');

          this.toast.error(this.errorMessage);
          this.reportValidationError(this.errorMessage);
          return;
        }

        const prevMaxExposure = extractBaseExposure(exposure) ?? 0;

        const totalAmount = this.roundAmount(
          disbursementRows.reduce(
            (total, row) => total + row.prpsdDsbmntAmt,
            0,
          ),
        );

        const payload: CalculateDisbursementRepaymentPlanPayload = {
          crdtPrpslSeq: proposalSequence,
          gracePeriod: termsSummary.grcPrd,
          numberOfInstallments: termsSummary.noOfInstlmnt,
          arrear: termsSummary.arrearFlg,
          frequency: termsSummary.rpymntFrqncy,
          prpsdMaxExpsr: this.roundAmount(prevMaxExposure + totalAmount),
          totalAmount,
          prevMaxExposure,
          terms: this.groupDisbursementsByTerm(disbursementRows),
        };

        const calculateSubscription = this.creditService
          .calculateDisbursementRepaymentPlan(payload)
          .pipe(
            finalize(() => {
              this.calculating = false;
            }),
          )
          .subscribe({
            next: (response: unknown) => {
              const plan =
                this.repaymentPlanState.publishCalculationResponse(response);

              if (!plan) {
                this.errorMessage =
                  this.translate.instant('DISB_REPAYMENT.ERR_INVALID_CALC_RESPONSE');

                this.toast.error(this.errorMessage);

                this.creditService.reportSectionSaveResult({
                  sectionId: this.sectionId,
                  status: 'api-error',
                  message: this.errorMessage,
                });

                return;
              }

              this.successMessage =
                this.translate.instant('DISB_REPAYMENT.CALCULATED_SUCCESS');

              this.toast.success(this.successMessage);
            },

            error: (error: any) => {
              this.errorMessage =
                error?.error?.message ??
                this.translate.instant('DISB_REPAYMENT.ERR_UNABLE_TO_CALCULATE');

              // ErrorInterceptor already toasts this HTTP error.
              console.error(
                'Calculate Repayment Plan API error:',
                error,
              );

              this.creditService.reportSectionSaveResult({
                sectionId: this.sectionId,
                status: 'api-error',
                message: this.errorMessage,
              });
            },
          });

        this.subscriptions.add(calculateSubscription);
      },

      error: (error: any) => {
        this.calculating = false;

        this.errorMessage =
          error?.error?.message ??
          this.translate.instant('DISB_REPAYMENT.ERR_UNABLE_TO_LOAD_INPUTS');

        // ErrorInterceptor already toasts this HTTP error.
        console.error(
          'Load Repayment Plan inputs error:',
          error,
        );
      },
    });

    this.subscriptions.add(inputsSubscription);
  }

  private extractTermsSummary(response: any): {
    grcPrd: number;
    noOfInstlmnt: number;
    rpymntFrqncy: number;
    arrearFlg: number;
  } | null {
    const source =
      response?.data &&
      typeof response.data === 'object' &&
      !Array.isArray(response.data)
        ? response.data
        : response;

    if (!source || typeof source !== 'object') {
      return null;
    }

    return {
      grcPrd: Number(source.grcPrd ?? 0),
      noOfInstlmnt: Number(source.noOfInstlmnt ?? 0),
      rpymntFrqncy: Number(source.rpymntFrqncy ?? 0),
      arrearFlg: Number(source.arrearFlg) === 1 ? 1 : 0,
    };
  }

saveRepaymentPlan(): void {
  this.errorMessage = '';
  this.successMessage = '';

  if (!this.editable) {
    return;
  }

  const proposalSequence =
    this.getCurrentProposalSequence();

  if (proposalSequence <= 0) {
    this.errorMessage =
      this.translate.instant('DISB_REPAYMENT.ERR_PROPOSAL_SEQ_REQUIRED');

    this.toast.error(this.errorMessage);
    this.reportValidationError(
      this.errorMessage,
    );

    return;
  }

  if (this.repaymentRows.length === 0) {
    this.errorMessage =
      this.translate.instant('DISB_REPAYMENT.ERR_CALCULATE_BEFORE_SAVE');

    this.toast.error(this.errorMessage);
    this.reportValidationError(
      this.errorMessage,
    );

    return;
  }

  const invalidSequenceRow =
    this.repaymentRows.find(
      (row) =>
        !Number.isFinite(
          Number(row.prpsdDsbmntSeq),
        ) ||
        Number(row.prpsdDsbmntSeq) <= 0,
    );

  if (invalidSequenceRow) {
    this.errorMessage =
      this.translate.instant('DISB_REPAYMENT.ERR_INVALID_DISBURSEMENT_SEQ');

    this.toast.error(this.errorMessage);
    this.reportValidationError(
      this.errorMessage,
    );

    return;
  }

  const invalidRow = this.repaymentRows.find(
    (row) => this.isRowTotalExceeded(row),
  );

  if (invalidRow) {
    this.errorMessage =
      this.translate.instant('DISB_REPAYMENT.ERR_TRANCHE_EXCEEDS', {
        tranche: invalidRow.prpsdDsbmntSeq,
        amount: this.formatAmount(invalidRow.trancheAmount),
      });

    this.toast.error(this.errorMessage);
    this.reportValidationError(
      this.errorMessage,
    );

    return;
  }

  if (this.isGrandTotalExceeded) {
    this.errorMessage =
      this.translate.instant('DISB_REPAYMENT.ERR_TOTAL_EXCEEDS');

    this.toast.error(this.errorMessage);
    this.reportValidationError(
      this.errorMessage,
    );

    return;
  }

  const payload =
    this.buildSavePayload(proposalSequence);

  console.log(
    'Save proposed repayment payload:',
    payload,
  );

  this.saving = true;

  const saveSubscription =
    this.repaymentPlanState
      .saveRepaymentPlan(payload)
      .pipe(
        finalize(() => {
          this.saving = false;
        }),
      )
      .subscribe({
        next: (response: unknown) => {
          // Backend can return HTTP 200 with an `{ "error": "..." }` body
          // instead of a proper error status — treat that the same as a
          // failed save rather than reporting success.
          const embeddedError =
            this.extractEmbeddedError(response);

          if (embeddedError) {
            this.errorMessage = embeddedError;

            // ErrorInterceptor already toasts this — it detects the
            // embedded `error` field on this same 200 response.
            console.error(
              'Save Repayment Plan API returned an error:',
              embeddedError,
            );

            this.creditService
              .reportSectionSaveResult({
                sectionId: this.sectionId,
                status: 'api-error',
                message: this.errorMessage,
              });

            return;
          }

          const savedPlan =
            this.buildCalculationPlan(
              proposalSequence,
            );

          this.repaymentPlanState
            .setCalculatedPlan(savedPlan);

          this.isDirty = false;

          this.successMessage =
            this.translate.instant('DISB_REPAYMENT.SAVED_SUCCESS');

          this.toast.success(this.successMessage);

          this.creditService
            .reportSectionSaveResult({
              sectionId: this.sectionId,
              status: 'saved',
            });
        },

        error: (error: any) => {
          console.error(
            'Save Repayment Plan API error:',
            error,
          );

          this.errorMessage =
            error?.error?.message ??
            error?.message ??
            this.translate.instant('DISB_REPAYMENT.ERR_UNABLE_TO_SAVE');

          // ErrorInterceptor already toasts this HTTP error.
          this.creditService
            .reportSectionSaveResult({
              sectionId: this.sectionId,
              status: 'api-error',
              message: this.errorMessage,
            });
        },
      });

  this.subscriptions.add(saveSubscription);
}

  private extractEmbeddedError(response: unknown): string | null {
    const message = (response as any)?.error;

    return typeof message === 'string' && message.trim()
      ? message.trim()
      : null;
  }

  private reportValidationError(message: string): void {
    this.creditService.reportSectionSaveResult({
      sectionId: this.sectionId,
      status: 'validation-error',
      message,
    });
  }

  openPreviewModal(): void {
    if (this.repaymentRows.length === 0) {
      return;
    }

    this.previewEditMode = false;
    this.showPreviewModal = true;
  }

  closePreviewModal(): void {
    this.showPreviewModal = false;
    this.previewEditMode = false;
  }

  togglePreviewEditMode(): void {
    if (!this.editable) {
      return;
    }

    this.previewEditMode = !this.previewEditMode;
  }

  formatPeriod(period: string): string {
    const parts = String(period).slice(0, 10).split('-');

    if (parts.length < 2) {
      return period;
    }

    const year = Number(parts[0]);
    const month = Number(parts[1]);

    if (!Number.isFinite(year) || !Number.isFinite(month)) {
      return period;
    }

    return new Intl.DateTimeFormat('en-GB', {
      month: 'short',
      year: '2-digit',
    }).format(new Date(year, month - 1, 1));
  }

  trackByColumn(index: number, column: string): string {
    return column;
  }

  trackByRepaymentRow(index: number, row: RepaymentRow): number {
    return row.prpsdDsbmntSeq || index;
  }

  private applyCalculatedPlan(plan: RepaymentCalculationResponse): void {
    const normalizedRows = Array.isArray(plan.trancheRows)
      ? plan.trancheRows.map((row) => ({
          prpsdDsbmntSeq: Number(row.prpsdDsbmntSeq ?? 0),
          quarter: String(row.quarter ?? ''),
          month: String(row.month ?? '').slice(0, 10),
          trancheAmount: this.roundAmount(
            Number(row.prpsdDsbmntAmount ?? 0),
          ),
          schedule: this.normalizeSchedule(
            row.installmentsByPeriod,
          ),
          termRefCdSeq: Number(row.rpymntTerm ?? 0),
        }))
      : [];

    this.repaymentRows = normalizedRows;
    this.columns = this.extractColumns(normalizedRows);
    this.totalDisbursementAmount = this.roundAmount(
      Number(plan.ttlDsbmnt ?? 0),
    );

    this.isDirty = false;
    this.errorMessage = '';
  }

  private normalizeSchedule(value: unknown): RepaymentSchedule {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return {};
    }

    return Object.entries(value as Record<string, unknown>)
      .filter(([period]) => /^\d{4}-\d{2}-\d{2}$/.test(period))
      .reduce<RepaymentSchedule>((schedule, [period, amount]) => {
        const numericAmount = Number(amount ?? 0);

        schedule[period] =
          Number.isFinite(numericAmount) && numericAmount >= 0
            ? this.roundAmount(numericAmount)
            : 0;

        return schedule;
      }, {});
  }

  private extractColumns(rows: RepaymentRow[]): string[] {
    const periods = new Set<string>();

    rows.forEach((row) => {
      Object.keys(row.schedule).forEach((period) => {
        periods.add(period);
      });
    });

    return Array.from(periods).sort((first, second) =>
      first.localeCompare(second),
    );
  }

 private buildSavePayload(
  proposalSequence: number,
): SaveRepaymentPlanPayload {
  return {
    crdtPrpslSeq: proposalSequence,
    terms: this.groupRepaymentRowsByTerm(this.repaymentRows),
  };
}

// Groups tranche rows by their repayment term into the `terms[]` shape
// the save API now expects.
private groupRepaymentRowsByTerm(
  rows: RepaymentRow[],
): SaveRepaymentPlanTermPayload[] {
  const groups = new Map<number, RepaymentCalculationTrancheRow[]>();

  rows.forEach((row) => {
    const key = Number(row.termRefCdSeq ?? 0);
    const list = groups.get(key) ?? [];

    list.push({
      quarter: row.quarter,

      month: String(row.month).slice(0, 10),

      prpsdDsbmntAmount: this.roundAmount(
        row.trancheAmount,
      ),

      prpsdDsbmntSeq: Number(
        row.prpsdDsbmntSeq,
      ),

      installmentsByPeriod: Object.entries(
        row.schedule,
      )
        .sort(([firstPeriod], [secondPeriod]) =>
          firstPeriod.localeCompare(secondPeriod),
        )
        .reduce<Record<string, number>>(
          (schedule, [period, amount]) => {
            schedule[period] = this.roundAmount(
              Number(amount ?? 0),
            );

            return schedule;
          },
          {},
        ),

      rpymntTerm: key,
    });

    groups.set(key, list);
  });

  return Array.from(groups.entries()).map(
    ([rpymntTerm, trancheRows]) => ({
      rpymntTerm,
      trancheRows,
      ttlDsbmnt: this.roundAmount(
        trancheRows.reduce(
          (total, row) => total + row.prpsdDsbmntAmount,
          0,
        ),
      ),
    }),
  );
}

  private buildCalculationPlan(
    proposalSequence: number,
  ): RepaymentCalculationResponse {
    return {
      crdtPrpslSeq: proposalSequence,
      ttlDsbmnt: this.totalDisbursementAmount,
      trancheRows: this.repaymentRows.map((row) => ({
        prpsdDsbmntSeq: row.prpsdDsbmntSeq,
        quarter: row.quarter,
        month: row.month,
        prpsdDsbmntAmount: row.trancheAmount,
        installmentsByPeriod: { ...row.schedule },
        rpymntTerm: row.termRefCdSeq,
      })),
    };
  }

  // Groups disbursement rows by their repayment term into the `terms[]`
  // shape the calculate API now expects.
  private groupDisbursementsByTerm(
    rows: ProposedDisbursementApiRow[],
  ): CalculateDisbursementRepaymentPlanTermPayload[] {
    const groups = new Map<
      number,
      CalculateDisbursementRepaymentPlanItemPayload[]
    >();

    rows.forEach((row) => {
      const key = Number(row.termRefCdSeq ?? 0);
      const list = groups.get(key) ?? [];

      list.push({
        schMth: row.schMth,
        prpsdDsbmntAmt: row.prpsdDsbmntAmt,
        prpsdDsbmntSeq: row.prpsdDsbmntSeq,
      });

      groups.set(key, list);
    });

    return Array.from(groups.entries()).map(
      ([rpymntTerm, disbursements]) => ({
        rpymntTerm,
        disbursements,
      }),
    );
  }

  private extractSavedDisbursements(
    response: unknown,
  ): ProposedDisbursementApiRow[] {
    const source =
      (response as any)?.data ?? response;

    const list = Array.isArray(source)
      ? source
      : Array.isArray(
            source?.prpsdDsbmntRowDTOList,
          )
        ? source.prpsdDsbmntRowDTOList
        : [];

    return list
      .filter(
        (item: any) =>
          Number(item?.delFlg ?? 0) !== 1,
      )
      .map(
        (
          item: any,
        ): ProposedDisbursementApiRow => ({
          prpsdDsbmntSeq: Number(
            item?.prpsdDsbmntSeq ?? 0,
          ),

          schMth: this.normalizeDateString(
            item?.schMth,
          ),

          prpsdDsbmntAmt: this.toAmount(
            item?.prpsdDsbmntAmt ??
              item?.prpsdDsbmtAmt,
          ),

          termRefCdSeq: Number(
            item?.rpymntTerm ??
              item?.typ ??
              item?.termRefCdSeq ??
              0,
          ),
        }),
      );
  }

  private normalizeDateString(
    value: unknown,
  ): string {
    const date = String(value ?? '').slice(0, 10);

    return /^\d{4}-\d{2}-\d{2}$/.test(date)
      ? date
      : '';
  }

  private toAmount(value: unknown): number {
    const amount = Number(value ?? 0);

    return Number.isFinite(amount) && amount >= 0
      ? this.roundAmount(amount)
      : 0;
  }

  getTermLabel(termRefCdSeq: number): string {
    if (!termRefCdSeq) {
      return '';
    }

    const option = this.termOptions.find(
      (item) => Number(item.value) === Number(termRefCdSeq),
    );

    return option?.label ? String(option.label).trim() : '';
  }

  private loadTermOptions(): void {
    const termsSubscription = this.pickListService
      .loadCodesAsync([PKL_GRP.FACILITY_TYPE])
      .subscribe({
        next: () => {
          // Repayment term types come from ref code group 13 (FACILITY_TYPE).
          this.termOptions = [
            ...this.pickListService.getOptionsSync(PKL_GRP.FACILITY_TYPE),
          ].sort(
            (first, second) =>
              Number(first.original?.sortOrder ?? 0) -
              Number(second.original?.sortOrder ?? 0),
          );
        },

        error: (error: unknown) => {
          console.error('Failed to load repayment terms:', error);
        },
      });

    this.subscriptions.add(termsSubscription);
  }

  /**
   * GET /rpymnt-plan/get-prpsd-rpymnt/{crdtPrpslSeq} returns a flat list of
   * individual installment rows (one disbursement tranche + one repayment
   * period + amount per entry) — not the grouped tranche-row shape the
   * Calculate button's response uses. Pairs it with the disbursements GET
   * (already used elsewhere in this component) to reconstruct the same
   * tranche-row plan and patches it into the shared `calculatedPlan$`
   * state, which the existing subscription in ngOnInit renders.
   */
  private loadSavedRepaymentPlan(sequence: number): void {
    if (this.loadedRepaymentPlanSequence === sequence) {
      return;
    }

    const loadSubscription = forkJoin({
      disbursements: this.creditService.getProposedDisbursements(sequence),
      repayments: this.creditService.getProposedRepayments(sequence),
    })
      .pipe(
        finalize(() => {
          this.loadedRepaymentPlanSequence = sequence;
        }),
      )
      .subscribe({
        next: ({ disbursements, repayments }) => {
          const plan = this.buildPlanFromSavedData(
            sequence,
            disbursements,
            repayments,
          );

          if (plan.trancheRows.length === 0) {
            // Nothing saved yet for this proposal — leave the
            // "not calculated yet" empty state as-is.
            return;
          }

          this.repaymentPlanState.setCalculatedPlan(plan);
        },

        error: (error: any) => {
          console.error(
            'Load saved Repayment Plan error:',
            error,
          );
        },
      });

    this.subscriptions.add(loadSubscription);
  }

  private buildPlanFromSavedData(
    sequence: number,
    disbursementsResponse: unknown,
    repaymentsResponse: unknown,
  ): RepaymentCalculationResponse {
    const disbursements = this.extractSavedDisbursements(
      disbursementsResponse,
    );

    const repayments = this.extractSavedRepayments(
      repaymentsResponse,
    );

    const scheduleByTranche = new Map<number, RepaymentSchedule>();
    const quarterByTranche = new Map<number, string>();

    repayments.forEach((repayment) => {
      const trancheSeq = repayment.prpsdDsbmntSeq;
      const period = repayment.schRpymntMth;

      if (trancheSeq <= 0 || !period) {
        return;
      }

      const schedule = scheduleByTranche.get(trancheSeq) ?? {};

      schedule[period] = this.roundAmount(
        (schedule[period] ?? 0) + repayment.prpsdRpymntAmnt,
      );

      scheduleByTranche.set(trancheSeq, schedule);

      if (repayment.qrtr && !quarterByTranche.has(trancheSeq)) {
        quarterByTranche.set(trancheSeq, repayment.qrtr);
      }
    });

    // Only tranches that actually have a saved schedule become rows —
    // a disbursement with no repayment installments yet must not show up
    // as an all-zero row.
    const trancheRows: RepaymentCalculationTrancheRow[] = disbursements
      .filter(
        (disbursement) =>
          disbursement.prpsdDsbmntSeq > 0 &&
          scheduleByTranche.has(disbursement.prpsdDsbmntSeq),
      )
      .map((disbursement) => ({
        prpsdDsbmntSeq: disbursement.prpsdDsbmntSeq,

        quarter: quarterByTranche.get(disbursement.prpsdDsbmntSeq) ?? '',

        month: disbursement.schMth,
        prpsdDsbmntAmount: disbursement.prpsdDsbmntAmt,
        installmentsByPeriod:
          scheduleByTranche.get(disbursement.prpsdDsbmntSeq) ?? {},
        rpymntTerm: disbursement.termRefCdSeq,
      }))
      .sort((first, second) => first.month.localeCompare(second.month));

    const ttlDsbmnt = this.roundAmount(
      trancheRows.reduce(
        (total, row) => total + row.prpsdDsbmntAmount,
        0,
      ),
    );

    return {
      crdtPrpslSeq: sequence,
      ttlDsbmnt,
      trancheRows,
    };
  }

  private extractSavedRepayments(
    response: unknown,
  ): SavedRepaymentInstallmentRow[] {
    const source = (response as any)?.data ?? response;

    const list = Array.isArray(source)
      ? source
      : Array.isArray(source?.prpsdRpymntRowDTOList)
        ? source.prpsdRpymntRowDTOList
        : [];

    return list
      .filter((item: any) => Number(item?.delFlg ?? 0) !== 1)
      .map(
        (item: any): SavedRepaymentInstallmentRow => ({
          prpsdDsbmntSeq: Number(item?.prpsdDsbmntSeq ?? 0),
          schRpymntMth: this.normalizeDateString(item?.schRpymntMth),
          prpsdRpymntAmnt: this.toAmount(item?.prpsdRpymntAmnt),
          qrtr: String(item?.qrtr ?? ''),
        }),
      );
  }

  private loadOpeningExposure(sequence: number): void {
    if (this.loadedOpeningExposureSequence === sequence) {
      return;
    }

    const exposureSubscription = this.creditService
      .getPreviousMaxExposure(sequence)
      .subscribe({
        next: (response: unknown) => {
          const exposure = extractBaseExposure(response);

          this.openingExposure = exposure ?? 0;
          this.loadedOpeningExposureSequence = sequence;
        },

        error: (error: any) => {
          console.error(
            'Previous maximum exposure API error:',
            error,
          );
        },
      });

    this.subscriptions.add(exposureSubscription);
  }

  private getCurrentProposalSequence(): number {
    const sequence = Number(
      this.crdtPrpslSeq ??
        this.creditService.getCrdtPrpslSeq() ??
        0,
    );

    return Number.isFinite(sequence) && sequence > 0
      ? sequence
      : 0;
  }

  private roundAmount(value: number): number {
    return Math.round((Number(value) + Number.EPSILON) * 100) / 100;
  }

  private formatAmount(value: number): string {
    return new Intl.NumberFormat('en-GB', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  }
}